import { useState, useCallback } from 'react';
import { DateFilterState, DatePreset } from '../types/metrics-type';

const getPresetDates = (preset: Omit<DatePreset, 'custom'>): { startDate: Date; endDate: Date } => {
  const now = new Date();
  const endDate = new Date(now);
  let startDate = new Date(now);

  switch (preset) {
    case 'last7days': {
      startDate.setDate(startDate.getDate() - 7);
      return { startDate, endDate };
    }
    case 'last30days': {
      startDate.setDate(startDate.getDate() - 30);
      return { startDate, endDate };
    }
    case 'last90days': {
      startDate.setDate(startDate.getDate() - 90);
      return { startDate, endDate };
    }
    case 'thisYear': {
      startDate = new Date(now.getFullYear(), 0, 1);
      return { startDate, endDate };
    }
    default:
      return { startDate: new Date(now), endDate: new Date(now) };
  }
};

export function useDateFilter(initialPreset: DatePreset = 'last30days') {
  const initialDates = getPresetDates(initialPreset);

  const [dateFilter, setDateFilter] = useState<DateFilterState>({
    preset: initialPreset,
    ...initialDates,
  });

  const setPreset = useCallback((preset: DatePreset) => {
    const dates = getPresetDates(preset);
    setDateFilter({
      preset,
      ...dates,
    });
  }, []);

  const setCustomDates = useCallback((startDate: Date, endDate: Date) => {
    setDateFilter({
      preset: 'custom',
      startDate,
      endDate,
    });
  }, []);

  const resetFilter = useCallback(() => {
    const dates = getPresetDates('last30days');
    setDateFilter({
      preset: 'last30days',
      ...dates,
    });
  }, []);

  return {
    dateFilter,
    setPreset,
    setCustomDates,
    lastWeekDates: getPresetDates('last7days'),
    lastMonthDates: getPresetDates('last30days'),
    resetFilter,
  };
}
